/**
 * =============================================================================
 * TOKEN EXPIRATION WARNING
 * =============================================================================
 *
 * Banner component displayed when the admin session token is about to expire.
 * Shows a warning 5 minutes before expiration with option to reload the page.
 *
 * PHASE 1 TASK: P1-14 - Token Expiration Tracking
 *
 * Features:
 * - Prominent warning banner when token expires within 5 minutes
 * - Displays time remaining until expiration
 * - Provides "Reload" button to refresh the session
 * - Dismissible to avoid blocking urgent work
 *
 * =============================================================================
 */

import React from 'react';
import { Box, Button, Text, Flex, FlexItem } from '@bigcommerce/big-design';
import { WarningIcon, CloseIcon } from '@bigcommerce/big-design-icons';

// =============================================================================
// TYPES
// =============================================================================

export interface TokenExpirationWarningProps {
  /**
   * Minutes remaining until token expires.
   */
  minutesRemaining: number | null;
  /**
   * Callback to dismiss the warning.
   */
  onDismiss: () => void;
  /**
   * ADR-025 B-2 (§3.1.4 half — `BL-ADMIN-M1-EXPIRY-DIRTY-GUARD-001`): when
   * true, the warning additionally states that unsaved settings changes exist
   * (they are lost on reload/expiry under the shell's keep-alive model).
   * OPTIONAL — the legacy layout passes nothing and renders byte-identically
   * (§3.5 rollback surface); only `AdminShell` wires it.
   */
  hasUnsavedSettings?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Warning banner displayed when admin session token is about to expire.
 *
 * @example
 * ```tsx
 * const { isExpiringSoon, minutesUntilExpiry, dismissExpiryWarning } = useSession();
 *
 * {isExpiringSoon && (
 *   <TokenExpirationWarning
 *     minutesRemaining={minutesUntilExpiry}
 *     onDismiss={dismissExpiryWarning}
 *   />
 * )}
 * ```
 */
export function TokenExpirationWarning({
  minutesRemaining,
  onDismiss,
  hasUnsavedSettings = false,
}: TokenExpirationWarningProps): React.ReactElement {
  /**
   * Reload the page to refresh the session.
   * BigCommerce will provide a new token on page load.
   */
  const handleReload = (): void => {
    window.location.reload();
  };

  // Format the expiration message
  const getExpirationMessage = (): string => {
    if (minutesRemaining === null || minutesRemaining <= 0) {
      return 'Your session has expired.';
    }
    if (minutesRemaining === 1) {
      return 'Your session will expire in 1 minute.';
    }
    return `Your session will expire in ${minutesRemaining} minutes.`;
  };

  return (
    <Box
      backgroundColor="warning10"
      padding="small"
      marginBottom="medium"
      borderRadius="normal"
      data-testid="token-expiration-warning"
    >
      <Flex alignItems="center" justifyContent="space-between">
        <FlexItem>
          <Flex alignItems="center">
            <FlexItem>
              <Box marginRight="xSmall">
                <WarningIcon color="warning50" size="large" />
              </Box>
            </FlexItem>
            <FlexItem>
              <Text color="secondary">
                {getExpirationMessage()}{' '}
                Reload the page to continue working.
              </Text>
              {/* ADR-025 B-2 (§3.1.4): the unsaved-settings AFFORDANCE only —
                  reloading (the remedy above) or letting the session expire
                  discards unsaved settings edits, so say so. Any deeper
                  §3.1.5 gate redesign (blocking, save-before-expiry) is
                  deliberately NOT built: it is E6-informed and E6 is still
                  operator-pending. */}
              {hasUnsavedSettings && (
                <Text
                  color="secondary"
                  bold
                  marginBottom="none"
                  data-testid="expiration-unsaved-settings-note"
                >
                  You have unsaved settings changes — save them first, or they will be lost when
                  the page reloads or the session expires.
                </Text>
              )}
            </FlexItem>
          </Flex>
        </FlexItem>
        <FlexItem>
          <Flex alignItems="center">
            <FlexItem marginRight="small">
              <Button
                variant="secondary"
                onClick={handleReload}
                data-testid="expiration-reload-button"
              >
                Reload Page
              </Button>
            </FlexItem>
            <FlexItem>
              <Button
                variant="subtle"
                iconOnly={<CloseIcon title="Dismiss warning" />}
                onClick={onDismiss}
                data-testid="expiration-dismiss-button"
              />
            </FlexItem>
          </Flex>
        </FlexItem>
      </Flex>
    </Box>
  );
}
